import { create } from 'zustand';
import { getUserProfile, fetchCreditPackages, CreditPackageInfo } from '@/lib/api';

interface UserState {
    credits: number;
    subscriptionCredits: number;
    purchasedCredits: number;
    creditPackages: CreditPackageInfo[];
    plan: string;
    planPeriodEnd: string | null;
    autoRenew: boolean;
    isLoading: boolean;
    hasLoaded: boolean;
    lastLoadedAt: number;
    fetchCredits: (options?: { force?: boolean; silent?: boolean; minIntervalMs?: number }) => Promise<void>;
    setCredits: (credits: number) => void;
}

let inFlightFetchCredits: Promise<void> | null = null;

export const useUserStore = create<UserState>((set, get) => ({
    credits: 0,
    subscriptionCredits: 0,
    purchasedCredits: 0,
    creditPackages: [],
    plan: 'free',
    planPeriodEnd: null,
    autoRenew: false,
    isLoading: false,
    hasLoaded: false,
    lastLoadedAt: 0,
    fetchCredits: async (options) => {
        const { force = false, silent = false, minIntervalMs = 15000 } = options ?? {};
        const currentState = get();
        const now = Date.now();

        if (!force && currentState.hasLoaded && now - currentState.lastLoadedAt < minIntervalMs) {
            return;
        }

        if (inFlightFetchCredits) {
            return inFlightFetchCredits;
        }

        const shouldShowLoading = !silent && !currentState.hasLoaded;
        if (shouldShowLoading) {
            set({ isLoading: true });
        }

        inFlightFetchCredits = (async () => {
            try {
                const [profile, packages] = await Promise.all([
                    getUserProfile(),
                    fetchCreditPackages().catch(() => [] as CreditPackageInfo[]),
                ]);
                if (typeof profile?.credits === 'number') {
                    set({
                        credits: profile.credits,
                        subscriptionCredits: profile.subscription_credits ?? 0,
                        purchasedCredits: profile.purchased_credits ?? 0,
                        creditPackages: packages,
                        plan: profile.plan ?? 'free',
                        planPeriodEnd: profile.plan_period_end ?? null,
                        autoRenew: Boolean(profile.auto_renew),
                        hasLoaded: true,
                        lastLoadedAt: Date.now(),
                    });
                }
            } catch (error: unknown) {
                const axiosError = error as { response?: { status?: number } };
                if (axiosError?.response?.status !== 401) {
                    console.error("Failed to fetch credits", error);
                }
            } finally {
                if (shouldShowLoading) {
                    set({ isLoading: false });
                }
                inFlightFetchCredits = null;
            }
        })();

        return inFlightFetchCredits;
    },
    setCredits: (credits) => set({ credits }),
}));
